import React, { useState } from 'react';
import { Alert, AlertTitle, Chip, Stack, Typography } from '@mui/material';

import type { CustomStep, PatientIntakeStep } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../hooks/index.js';
import {
  UploadFileModal,
  useFileUploader,
  generateUploadPromises,
  type UploadFilesFormData,
  type FileUploadMetadata,
} from '../../../../file-upload';
import { usePatientIntakeStatusContext } from '../../hooks';
import { NoteDisplay, ViewDocumentModal } from '../../components';
import type { BaseActionPromptProps } from './CustomSteps.types.ts';
import { CustomStepActionPrompt } from './CustomStepActionPrompt.tsx';

type UploadMode = {
  isOpen: true;
  healthCategoryId: string;
  stepId: string;
  hint: {
    title: string;
    description: string;
    note: NonNullable<PatientIntakeStep['currentStatus']>['note'] | null;
  };
  documentType: Extract<
    FileUploadMetadata['PATIENT_DOCUMENT_UPLOAD'],
    { type: 'INTAKE_REQUEST' }
  >['data']['documentType'];
};

type UploadFileModalDescriptionProps = {
  uploadMode: UploadMode;
};

const UploadFileModalDescription: React.FC<UploadFileModalDescriptionProps> = ({ uploadMode }) => (
  <Alert severity="info" icon={false}>
    <AlertTitle>{uploadMode.hint?.title}</AlertTitle>
    <Typography variant="body2">{uploadMode.hint?.description}</Typography>

    {!!uploadMode.hint?.note && <NoteDisplay stepId={uploadMode.stepId} noteConfig={uploadMode.hint?.note} />}
  </Alert>
);

type UploadModalProps = {
  uploadMode: UploadMode | null;
  setUploadMode: React.Dispatch<React.SetStateAction<UploadMode | null>>;
};

const UploadModal: React.FC<UploadModalProps> = ({ uploadMode, setUploadMode }) => {
  const { user, userType } = useAuth();
  const { uploader } = useFileUploader();

  const handleFileUploadOnSubmit = async ({ files, description }: UploadFilesFormData) => {
    if (!uploadMode) {
      throw new Error('Upload mode not found');
    }

    if (!user || !userType || !user.email) {
      throw new Error('User not found');
    }

    const { healthCategoryId, stepId, documentType } = uploadMode;

    if (!healthCategoryId || !stepId) {
      throw new Error('Category ID or step ID not found');
    }

    const generateFileUploadData: Parameters<
      typeof generateUploadPromises<'PATIENT_DOCUMENT_UPLOAD'>
    >[0]['generateFileUploadData'] = ({ file }) => ({
      file,
      type: 'PATIENT_DOCUMENT_UPLOAD',
      ownerId: user.uid,
      uploaderId: user.uid,
      fileName: file.name,
      metadata: {
        patientId: user.uid,
        description: description.trim(),
        type: 'INTAKE_REQUEST',
        data: {
          stepId,
          documentType,
          healthCategoryId,
        },
      },
    });

    return await generateUploadPromises({ files, uploader, generateFileUploadData });
  };

  const handleCloseOnClick = () => {
    setUploadMode(null);
  };

  if (!uploadMode) {
    return null;
  }

  return (
    <UploadFileModal
      isOpen
      noToast
      handleCloseOnClick={handleCloseOnClick}
      onSubmit={handleFileUploadOnSubmit}
      maxFiles={1}
      description={<UploadFileModalDescription uploadMode={uploadMode} />}
    />
  );
};

type UploadDocumentActionModalProps = {
  step: CustomStep['DOCUMENT_UPLOAD'];
  uploadMode: UploadMode | null;
  setUploadMode: React.Dispatch<React.SetStateAction<UploadMode | null>>;
};

const UploadDocumentActionModal: React.FC<UploadDocumentActionModalProps> = ({ step, uploadMode, setUploadMode }) => {
  const { status } = step.currentStatus;

  if (status === 'COMPLETED') {
    return (
      <ViewDocumentModal
        step={step}
        type="PATIENT_DOCUMENT_UPLOAD"
        isOpen={!!uploadMode}
        onClose={() => setUploadMode(null)}
      />
    );
  }

  return <UploadModal uploadMode={uploadMode} setUploadMode={setUploadMode} />;
};

type ActionDescriptionProps = {
  description: string;
  documentType: NonNullable<
    Extract<FileUploadMetadata['PATIENT_DOCUMENT_UPLOAD'], { type: 'INTAKE_REQUEST' }>['data']
  >['documentType'];
};

const ActionDescription: React.FC<ActionDescriptionProps> = ({ description, documentType }) => {
  const friendlyDocumentType = documentType.replace(/_/g, ' ').toLowerCase();

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      gap={{ xs: 0, sm: 0.5 }}
    >
      <Chip
        label={friendlyDocumentType}
        size="small"
        variant="outlined"
        sx={{ textTransform: 'capitalize', fontSize: '0.75rem', height: 'fit-content', px: 0 }}
      />
      <Typography variant="body2">{description}</Typography>
    </Stack>
  );
};

interface UploadDocumentActionProps extends BaseActionPromptProps {
  step: CustomStep['DOCUMENT_UPLOAD'];
  activeHealthCategoryId: string;
}

export const UploadDocumentAction: React.FC<UploadDocumentActionProps> = ({
  step,
  activeHealthCategoryId,
  onEdit,
  onDelete,
  onToggleDisabled,
}) => {
  const { customSteps } = usePatientIntakeStatusContext();
  const [uploadMode, setUploadMode] = useState<UploadMode | null>(null);

  const handleOnClick = () => {
    const { metadata } = step;

    const { documentType } = metadata;

    setUploadMode({
      documentType,
      stepId: step.id,
      isOpen: true,
      healthCategoryId: activeHealthCategoryId,
      hint: {
        title: step.title,
        description: step.description,
        note: step.currentStatus?.note ?? null,
      },
    });
  };

  if (!customSteps) {
    return null;
  }

  return (
    <>
      <CustomStepActionPrompt
        step={step}
        description={<ActionDescription description={step.description} documentType={step.metadata.documentType} />}
        onClick={handleOnClick}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleDisabled={onToggleDisabled}
      />

      <UploadDocumentActionModal step={step} uploadMode={uploadMode} setUploadMode={setUploadMode} />
    </>
  );
};
